<template>
    <v-card style="height:100%">
      <v-card-title>
        <ent-workflow-nav-bar
          :args="wNav"
          :buttonclick.sync="navbuttons"
          :title="wTitle"
        ></ent-workflow-nav-bar>
      </v-card-title>
      <v-card-text>
        <v-row no-gutters dense>
          <v-col v-if="heroPanel" cols="auto" md="8" sm="12">
            <ent-workflow-welcome :args="heroText"> </ent-workflow-welcome>
          </v-col>
          <v-col v-if="searchPanel" cols="auto" md="4" sm="12"
            ><ent-search-config
              :args="searchPanelArgs"
              :selection.sync="searchPanelData"
            ></ent-search-config
          ></v-col>
          <v-col v-if="mainPanel" cols="auto" md="8" sm="12">
            <schema-form
              :args="mainPanelArgs"
              :saveok.sync="mainPanelSave"
              :formStyle="mainPanelStyle"
              v-model="mainPanelData"
            ></schema-form>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </template>
  <script setup>
    // Components used in this view
    import EntWorkflowNavBar from "@/../../library/view/EntWorkflowNavBar.vue";
    import EntWorkflowWelcome from "@/../../library/view/EntWorkflowWelcome.vue";
    import EntSearchConfig from "@/../../library/view/EntSearchConfig.vue";
    import SchemaForm from "@/../../library/view/SchemaForm.vue";
  
    // Component functions
    import { useWorkflowController } from "@/../../library/compFunctions/useWorkflowController.js";
    import {
      fullSize,
      card800,
    } from "@/../../library/compFunctions/useStyles.js";
    import {
      startToast,
      endToast,
    } from "@/../../cortex/src/compFunctions/useToasted.js";
  
    // Services used
    import { create, update } from "@/../../cortex/src/services/cortex.js";
  
    // Helper Functions
    import check from "@/../../library/helper/propertyCheck.js";
    const CONSTANTS = require("@/../../library/constants/server.js");
  
    // Vue helper functions
    import { onMounted, ref, watch, getCurrentInstance } from "vue";
  
    // Define the properties
    const props = defineProps({ args: { type: Object, required: false } });
  
    // Define the emits of this flow
    const emit = defineEmits(["update:finished"]);
  
    // Define the variables for the main panel
    let mainPanelSave = ref(false);
    let mainPanelArgs = ref({
      schema: "sch_set_groups",
      datatable: "groups",
      item: "",
      dataserver: CONSTANTS.ORIG,
    });
    let mainPanelFinish = ref(null);
    let mainPanelStyle = ref(null);
    let mainPanelData = ref({});
    let mainPanelDataSet = ref({});
  
    // Define the variables for the search panel
    let searchPanelArgs = { panelTitle: "edit_select", selectLabel: "Title" };
    let searchPanelData = ref("");
    // Get the VM instance - needed for some of the plugins
    const vm = getCurrentInstance();
    console.log(`******** VM = `, vm);
  
    // Prepare the workflow configuration based on the controller properties
    const {
      config: wNav,
      mode: mode,
      hero: heroPanel,
      search: searchPanel,
      main: mainPanel,
    } = useWorkflowController();
  
    let wTitle = ref("");
  
    // Create the hero description text
    let heroText = props.args?.desc;
  
    //TODO: Check for help when workflows start having help files
  
    // Set the default state of the widget on mount
    onMounted(() => {
      console.log("%%%%%%% Release Top Level Mounted");
      console.log(`Props: `, props);
      wTitle.value = props.args?.title;
      if (props.args.hasOwnProperty("popup") && props.args?.popup) {
        if (
          props.args.hasOwnProperty("item") &&
          !check.isEmptyString(props.args.item)
        ) {
          mode.value = "popup_approve_edit";
          console.log(`Edit ID:`, props.args?.item);
          mainPanelArgs.value.item = props.args?.item;
        } else {
          mode.value = "popup_approve_create";
        }
        mainPanelStyle.value = card800();
      } else {
        mode.value = "welcome";
        mainPanelStyle.value = fullSize();
      }
    });
  
    // Create the variable to handle the navigation bar button clicks
    const navbuttons = ref(null);
  
    // Process changes to navbuttons
    watch(navbuttons, async () => {
      let button = navbuttons.value.click;
      console.log(`Nav Button Click `, button);
      if (button === "close") closeClick();
      else if (button === "new") newClick();
      else if (button === "cancel") cancelClick();
      else if (button === "save") await saveClick(false);
      else if (button === "approve") await saveClick(true)
    });
  
    // Process what happens when the save button is enabled
    watch(mainPanelSave, () => {
      console.log(`&&&&&&& SAVE Enabled`);
      mode.value = "approveOK";
    });
  
    // New Button Click
    const newClick = () => {
      mode.value = "approve_create";
    };
  
    // Save Button Click
    const saveClick = async (approval) => {
      console.log(`@@@@@@ Save Clicked, data: `, mainPanelData.value.payload);
      if (!mainPanelData.value.payload.hasOwnProperty("_id")) await createNote(approval);
      else {
        console.log(`%%%%%%% Update of an existing Record`);
        await updateNote(approval);
      }
      cancelClick();
    };
  
    // Edit Button Click
    const editClick = () => {
      mode.value = "approve_edit";
    };
  
    // Cancel Button Click
    const cancelClick = () => {
      if (!props.args?.popup) mode.value = "welcome";
      else closeClick();
    };
  
    // Close Button Click
    const closeClick = () => {
      if (!props.args?.popup) vm.proxy.$router.go(-1);
      emit("update:finished", true);
    };
  
    // Functions to work with the record
    // Function to save a note
    const createNote = async (approval) => {
      startToast(vm, "Saving New Record");
      let payload = mainPanelData.value.payload
      if(approval) payload["approved"] = "yes"
      let saveResult = await create(
        mainPanelArgs.value.datatable,
        payload
      );
      console.log(`Trying to Stop the loader`);
      endToast(vm, "Record Saved");
    };
  
    // Function to update a note
    const updateNote = async (approval) => {
      startToast(vm, "Saving Record Update");
      let payload = mainPanelData.value.payload
      if(approval) payload["approved"] = "yes"
      let updateResult = await update(
        mainPanelArgs.value.datatable,
        payload
      );
      endToast(vm, "Record Updated");
    };
  </script>
  